// src/components/EditPanels/PersonEditPanel.js
import React, { useRef, useEffect } from 'react';
import { Heart, Users, X, Trash2, Link2, Baby, UserX, Search } from 'lucide-react';
import { useGenogram } from '../../contexts/GenogramContext';
import { usePersonActions } from '../../hooks/usePersonActions';
import { calculateAge } from '../../utils/age';
import { normalizeDeceasedSelection } from '../../utils/deceasedSymbolHelpers';
import DeceasedSymbolPicker from '../common/DeceasedSymbolPicker';
import AddChildButton from '../UI/AddChildButton';

const PersonEditPanel = () => {
  const { state, actions } = useGenogram();
  const { selectedPerson, relationships } = state;
  const { 
    createSpouse, 
    createParents, 
    createChild,
    startConnectionFromPerson,
    deleteSelectedPerson,
    disconnectChildFromParents
  } = usePersonActions();
  
  const nameInputRef = useRef(null);
  const ageInputRef = useRef(null);

  useEffect(() => {
    if (selectedPerson && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [selectedPerson?.id]);

  if (!selectedPerson) return null;

  const { symbol: deceasedSymbolValue, gentle: deceasedGentleValue } = normalizeDeceasedSelection(selectedPerson);

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter' && ageInputRef.current) {
      e.preventDefault();
      ageInputRef.current.focus();
      // Don't try to select text in number input - just focus it
      ageInputRef.current.select();
    }
  };

  const updatePerson = (updates) => {
    actions.updatePerson(selectedPerson.id, updates);
  };

  const hasParents = relationships.some(r => r.type === 'child' && r.to === selectedPerson.id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Name */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Full Name
        </label>
        <input
          ref={nameInputRef}
          type="text"
          value={selectedPerson.name}
          onChange={(e) => updatePerson({ name: e.target.value })}
          onKeyDown={handleNameKeyDown}
          style={{
            width: '100%',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            backgroundColor: '#f9fafb'
          }}
        />
      </div>

      {/* Gender Identity */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Gender Identity
        </label>
        <select
          id="gender-identity-select"
          value={selectedPerson.gender}
          onChange={(e) => updatePerson({ gender: e.target.value })}
          style={{
            width: '100%',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            backgroundColor: '#f9fafb'
          }}
        >
          <option value="male">Male (Square)</option>
          <option value="female">Female (Circle)</option>
          <option value="non-binary">Non-Binary (Diamond)</option>
          <option value="transgender-male">Transgender Male</option>
          <option value="transgender-female">Transgender Female</option>
          <option value="genderfluid">Genderfluid (Triangle)</option>
          <option value="agender">Agender (Hexagon)</option>
          <option value="unknown">Unknown (?)</option>
        </select>
      </div>

      {/* Sexual Orientation */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Sexual Orientation
        </label>
        <select
          value={selectedPerson.sexualOrientation || 'not-specified'}
          onChange={(e) => updatePerson({ sexualOrientation: e.target.value })}
          style={{
            width: '100%',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            backgroundColor: '#f9fafb'
          }}
        >
          <option value="not-specified">Not Specified</option>
          <option value="heterosexual">Heterosexual</option>
          <option value="gay">Gay</option>
          <option value="lesbian">Lesbian</option>
          <option value="bisexual">Bisexual</option>
          <option value="pansexual">Pansexual</option>
          <option value="asexual">Asexual</option>
          <option value="queer">Queer</option>
          <option value="questioning">Questioning</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Birth Date and Age */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
            Birth Date
          </label>
          <input
            type="date"
            value={selectedPerson.birthDate || ''}
            onChange={(e) => {
              const updates = { birthDate: e.target.value };
              if (e.target.value) {
                const ageYears = calculateAge(
                  e.target.value,
                  selectedPerson.deathDate,
                  selectedPerson.isDeceased
                );
                updates.age = ageYears;
              }
              updatePerson(updates);
            }}
            style={{
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              backgroundColor: '#f9fafb'
            }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
            Age
          </label>
          <input
            ref={ageInputRef}
            type="number"
            value={selectedPerson.age || ''}
            onChange={(e) => updatePerson({ age: e.target.value })}
            style={{
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              backgroundColor: '#f9fafb'
            }}
          />
        </div>
      </div>

      {/* Deceased checkbox */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px' }}>
        <input
          type="checkbox"
          id="deceased"
          checked={selectedPerson.isDeceased}
          onChange={(e) => {
            const checked = e.target.checked;
            const updates = { isDeceased: checked };
            if (checked && !selectedPerson.deceasedSymbol) {
              updates.deceasedSymbol = 'halo';
            }
            updatePerson(updates);
          }}
          style={{ width: '16px', height: '16px' }}
        />
        <label htmlFor="deceased" style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
          Person is deceased
        </label>
      </div>

      {/* Date Deceased (appears when deceased is checked) */}
      {selectedPerson.isDeceased && (
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
            Date Deceased
          </label>
          <input
            type="date"
            value={selectedPerson.deathDate || ''}
            onChange={(e) => {
              const updates = { deathDate: e.target.value };
              if (selectedPerson.birthDate && e.target.value) {
                const ageYears = calculateAge(
                  selectedPerson.birthDate,
                  e.target.value,
                  true
                );
                updates.age = ageYears;
              }
              updatePerson(updates);
            }}
            style={{
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              backgroundColor: '#f9fafb'
            }}
          />
        </div>
      )}

      {selectedPerson.isDeceased && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Remembrance symbol</span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>Optional</span>
          </div>
          <DeceasedSymbolPicker
            symbolValue={deceasedSymbolValue}
            gentleValue={deceasedGentleValue}
            onChange={(symbol, gentle) =>
              updatePerson({
                deceasedSymbol: symbol,
                deceasedGentleTreatment: gentle
              })
            }
          />
        </div>
      )}

      {/* Special Status */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Special Status
        </label>
        <select
          value={selectedPerson.specialStatus || 'none'}
          onChange={(e) => updatePerson({ specialStatus: e.target.value === 'none' ? null : e.target.value })}
          style={{
            width: '100%',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            backgroundColor: '#f9fafb'
          }}
        >
          <option value="none">None</option>
          <option value="adopted">Adopted</option>
          <option value="foster">Foster</option>
          <option value="unborn">Unborn/Fetus</option>
        </select>
      </div>

      {/* Pregnancy Status - Available for all genders except male */}
      {selectedPerson.gender !== 'male' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', backgroundColor: '#fef3c7', borderRadius: '12px' }}>
            <input
              type="checkbox"
              id="is-pregnant"
              checked={selectedPerson.isPregnant || false}
              onChange={(e) => updatePerson({ isPregnant: e.target.checked })}
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="is-pregnant" style={{ fontSize: '14px', fontWeight: '500', color: '#92400e' }}>
              Currently Pregnant
            </label>
          </div>

          {/* Pregnancy Details */}
          {selectedPerson.isPregnant && (
            <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#fffbeb', borderRadius: '12px', border: '1px solid #fde68a' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#92400e', marginBottom: '6px' }}>
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={selectedPerson.dueDate || ''}
                    onChange={(e) => updatePerson({ dueDate: e.target.value })}
                    style={{
                      width: '100%',
                      border: '1px solid #fde68a',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#92400e', marginBottom: '6px' }}>
                    Weeks
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="42"
                    value={selectedPerson.gestationalWeeks || ''}
                    onChange={(e) => updatePerson({ gestationalWeeks: e.target.value })}
                    placeholder="e.g., 28"
                    style={{
                      width: '100%',
                      border: '1px solid #fde68a',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#92400e', marginBottom: '6px' }}>
                  Pregnancy Notes
                </label>
                <textarea
                  value={selectedPerson.pregnancyNotes || ''}
                  onChange={(e) => updatePerson({ pregnancyNotes: e.target.value })}
                  placeholder="e.g., twins expected, high-risk pregnancy..."
                  style={{
                    width: '100%',
                    height: '60px',
                    border: '1px solid #fde68a',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    backgroundColor: 'white',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Network Member */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', backgroundColor: '#ecfdf5', borderRadius: '12px' }}>
        <input
          type="checkbox"
          id="network-member"
          checked={selectedPerson.networkMember || false}
          onChange={(e) => updatePerson({ networkMember: e.target.checked })}
          style={{ width: '16px', height: '16px' }}
        />
        <label htmlFor="network-member" style={{ fontSize: '14px', fontWeight: '500', color: '#065f46' }}>
          Network Member
        </label>
      </div>

      {/* Role and Notes (if network member) */}
      {selectedPerson.networkMember && (
        <>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Role
            </label>
            <input
              type="text"
              value={selectedPerson.role || ''}
              onChange={(e) => updatePerson({ role: e.target.value })}
              placeholder="e.g., Therapist, Case Worker..."
              style={{
                width: '100%',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                backgroundColor: '#f9fafb'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Network Notes
            </label>
            <textarea
              value={selectedPerson.networkNotes || ''}
              onChange={(e) => updatePerson({ networkNotes: e.target.value })}
              style={{
                width: '100%',
                height: '80px',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                backgroundColor: '#f9fafb',
                resize: 'vertical'
              }}
            />
          </div>
        </>
      )}

      {/* Quick Actions */}
      <div style={{ paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
          Quick Actions
        </h4>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={() => actions.setFamilyFinderOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: '#dbeafe',
              color: '#1d4ed8',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Search size={16} />
            Family Finder
          </button>

          <button
            onClick={createSpouse}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: '#e0e7ff',
              color: '#6366f1',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Heart size={16} />
            Add Spouse/Partner
          </button>

          <button
            onClick={() => startConnectionFromPerson('marriage')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: '#ede9fe',
              color: '#8b5cf6',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Link2 size={16} />
            Connect to Another Person
          </button>

          {!hasParents && (
            <button
              onClick={createParents}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                backgroundColor: '#fef3c7',
                color: '#92400e',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <Users size={16} />
              Create Parents
            </button>
          )}
          
          {hasParents && (
            <button
              onClick={disconnectChildFromParents}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                backgroundColor: '#fef3c7',
                color: '#d97706',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <UserX size={16} />
              Disconnect from Parents
            </button>
          )}
          {console.log('PersonEditPanel rendering, selectedPerson:', selectedPerson)}
          {console.log('AddChildButton imported?', typeof AddChildButton)}

          <AddChildButton person={selectedPerson} />

          <button 
            onClick={deleteSelectedPerson}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              marginTop: '8px'
            }}
          >
            <Trash2 size={16} />
            Delete Person
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonEditPanel;